import {useTheme} from "@emotion/react";
import Markdown from "@/elements/Markdown";
import Reveal from "@/elements/Reveal";
import {Text, textStyles} from "@/elements/text";
import type {SectionImageTextInserts} from "@/generated/api";
import SectionWrapper from "@/layouts/SectionWrapper";
import {theme} from "@/styles/theme";
import {imageOf} from "@/utils/index";
import {EmojiText} from "./EmojiText";
import {CardWrapper, ImageWrapper, TextWrapper} from "./styles";

interface B9ImageTextEncartsProps {
  sectionData: SectionImageTextInserts;
  isFirstSection?: boolean;
  isLastSection?: boolean;
  id?: string;
}

const B9ImageTextEncarts = ({
  sectionData,
  isFirstSection,
  isLastSection,
  id,
}: B9ImageTextEncartsProps) => {
  const {bg_color, img_position, text} = sectionData;
  // WordPress answers `false` for a repeater with no row
  const inserts = Array.isArray(sectionData.inserts) ? sectionData.inserts : [];
  const image = imageOf(sectionData.image);
  const isLeftPosition = img_position === "left";
  const {breakpoints} = useTheme();
  const {isMobile} = breakpoints;
  return (
    <SectionWrapper
      id={id}
      backgroundColor={bg_color}
      isFirstSection={isFirstSection}
      isLastSection={isLastSection}
      styles={
        isLeftPosition
          ? {
              paddingLeft: 0,
              paddingBlock: "10px 10px",
              overflow: isMobile ? "hidden" : "initial",
            }
          : {
              paddingRight: 0,
              paddingBlock: "10px 10px",
              overflow: isMobile ? "hidden" : "initial",
            }
      }
      innerWrapperStyles={{paddingInline: 0, alignItems: "flex-start"}}
    >
      {(isLeftPosition || isMobile) && (
        <ImageWrapper
          imageUrl={image?.url}
          isLeftPosition={isLeftPosition}
          credit={image?.caption || ""}
          aria-label={image?.alt || "image and text block with inserts"}
          title={image?.title || "image and text block with inserts"}
        />
      )}

      <TextWrapper isLeftPosition={isLeftPosition} isMobile={isMobile}>
        {!!(text ? text.length : 0) && (
          <Reveal
            from={{opacity: 0, transform: "translateY(200px)"}}
            to={{opacity: 1, transform: "translateY(0px)"}}
            once
          >
            <div style={{marginBlock: isMobile ? "35px 30px" : "20px 64px"}}>
              <Markdown wrapperStyles={{marginTop: 0, marginBottom: 27}}>{text}</Markdown>
            </div>
          </Reveal>
        )}

        {inserts.length > 0 &&
          inserts.map((card) => {
            return (
              <Reveal
                key={card.first_part_title}
                from={{opacity: 0, transform: "translateY(200px)"}}
                to={{opacity: 1, transform: "translateY(0px)"}}
                once
              >
                <div>
                  {!!card?.surtitle?.length && (
                    <Text type="titleExtraSmall" styles={{marginBottom: 24}}>
                      {card.surtitle}
                    </Text>
                  )}

                  <CardWrapper styles={{marginBottom: 22}}>
                    <EmojiText title={card?.first_part_title} emoji={card?.emoji} />

                    <Markdown
                      styles={{
                        marginTop: "16px",
                      }}
                      textStyles={{
                        ...textStyles({...theme, breakpoints}).bodyRegular,
                      }}
                    >
                      {card?.text}
                    </Markdown>
                  </CardWrapper>
                </div>
              </Reveal>
            );
          })}
      </TextWrapper>
      {!isLeftPosition && !isMobile && (
        <ImageWrapper
          imageUrl={image?.url}
          isLeftPosition={isLeftPosition}
          isMobile={isMobile}
          credit={image?.caption || ""}
          aria-label={image?.alt || "image and text block with inserts"}
          title={image?.title || "image and text block with inserts"}
        />
      )}
    </SectionWrapper>
  );
};

export default B9ImageTextEncarts;
